import React from "react";

export type Subtract<T, K> = Omit<T, keyof K>;

// Type helpers taken from redux types definitions

export type GetProps<C> = C extends React.ComponentType<infer P> ? P : never;
/**
 * A property P will be present if:
 * - it is present in DecorationTargetProps
 *
 * Its value will be dependent on the following conditions
 * - if property P is present in InjectedProps and its definition extends the definition
 *   in DecorationTargetProps, then its definition will be that of DecorationTargetProps[P]
 * - if property P is not present in InjectedProps then its definition will be that of
 *   DecorationTargetProps[P]
 * - if property P is present in InjectedProps but does not extend the
 *   DecorationTargetProps[P] definition, its definition will be that of InjectedProps[P]
 */
export type Matching<InjectedProps, DecorationTargetProps> = {
  [P in keyof DecorationTargetProps]: P extends keyof InjectedProps
    ? InjectedProps[P] extends DecorationTargetProps[P]
      ? DecorationTargetProps[P]
      : InjectedProps[P]
    : DecorationTargetProps[P];
};

export function nonNull<T>(x: T | null): x is T {
  return x !== null;
}

export function assertIsDefined<T>(value: T): asserts value is NonNullable<T> {
  if (value === undefined || value === null) {
    throw new Error(`Expected 'value' to be defined, but received ${value}`);
  }
}

export function isSome<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export const zip = <T, U>(arr1: T[], arr2: U[]): [T, U | undefined][] =>
  arr1.map((t, i) => [t, arr2?.[i]]);

// How to use it :
// You have an interface/type with 2 optional fields and you want to enforce that at least one of them is present :
// interface User {
//   name: string;
//   email?: string;
//   phone?: string;
// }
// type UserEnforced = RequireAtLeastOne<User, 'email' | 'phone'>
// Now you have to provide at least one of the 2 fields : email or phone
// More info in this playground : https://t.ly/-YAs
export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

export type NonEmptyArray<T> = [T, ...T[]];

export type ValueOf<T> = T[keyof T];

export const assertNever = (value: never, noThrow: boolean = false): never => {
  if (noThrow) {
    return value;
  }
  throw new Error(`Unhandled discriminated union member: ${JSON.stringify(value)}`);
};
